import { useBrowserStore, ScriptStep } from '@/stores/browserStore';
import {
  Circle,
  Square,
  Play,
  Trash2,
  Save,
  FolderOpen,
  X,
  GripVertical,
  Clock,
} from 'lucide-react';
import { useState } from 'react';

function StepItem({
  step,
  index,
  isPlaying,
  isCurrentStep,
  onRemove,
  onUpdateDelay,
}: {
  step: ScriptStep;
  index: number;
  isPlaying: boolean;
  isCurrentStep: boolean;
  onRemove: () => void;
  onUpdateDelay: (delay: number) => void;
}) {
  const [editingDelay, setEditingDelay] = useState(false);
  const [showScreenshot, setShowScreenshot] = useState(false);

  return (
    <>
      <div
        className={`flex items-center gap-2 px-2 py-1.5 rounded text-xs transition-colors ${
          isCurrentStep ? 'bg-primary/20 border border-primary/50' : 'hover:bg-secondary/50'
        }`}
      >
        <GripVertical className="h-3 w-3 text-muted-foreground cursor-grab" />
        <span className="text-muted-foreground font-mono w-5">{index + 1}</span>
        {step.screenshot && (
          <button
            onClick={() => setShowScreenshot(true)}
            className="flex-shrink-0 rounded overflow-hidden border border-border hover:border-primary transition-colors"
            title="View screenshot"
          >
            <img
              src={step.screenshot}
              alt={`Step ${index + 1}`}
              className="w-10 h-6 object-cover object-top"
            />
          </button>
        )}
        <span className="flex-1 text-foreground truncate">{step.action.preview}</span>
        {editingDelay ? (
          <input
            type="number"
            value={step.delay}
            onChange={(e) => onUpdateDelay(Number(e.target.value))}
            onBlur={() => setEditingDelay(false)}
            onKeyDown={(e) => e.key === 'Enter' && setEditingDelay(false)}
            className="w-16 bg-secondary border-0 rounded px-1 py-0.5 text-xs text-foreground"
            autoFocus
          />
        ) : (
          <button
            onClick={() => setEditingDelay(true)}
            className="flex items-center gap-1 text-muted-foreground hover:text-foreground"
            title="Edit delay"
          >
            <Clock className="h-3 w-3" />
            <span>{step.delay}ms</span>
          </button>
        )}
        {!isPlaying && (
          <button
            onClick={onRemove}
            className="p-0.5 rounded hover:bg-secondary text-muted-foreground hover:text-red-400"
            title="Remove step"
          >
            <X className="h-3 w-3" />
          </button>
        )}
      </div>

      {/* Screenshot modal */}
      {showScreenshot && step.screenshot && (
        <div
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-8"
          onClick={() => setShowScreenshot(false)}
        >
          <div className="relative max-w-full max-h-full">
            <button
              onClick={() => setShowScreenshot(false)}
              className="absolute -top-10 right-0 p-2 rounded bg-secondary text-foreground hover:bg-secondary/80"
            >
              <X className="h-5 w-5" />
            </button>
            <div className="text-center mb-2 text-sm text-muted-foreground">
              Step {index + 1}: {step.action.preview}
            </div>
            <img
              src={step.screenshot}
              alt={`Step ${index + 1}`}
              className="max-w-full max-h-[80vh] rounded-lg shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}
    </>
  );
}

function SaveDialog({
  onSave,
  onCancel,
}: {
  onSave: (name: string) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState('');

  return (
    <div className="p-3 border-t border-border">
      <p className="text-xs text-muted-foreground mb-2">Save script as:</p>
      <div className="flex gap-2">
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Script name..."
          className="flex-1 bg-secondary border-0 rounded px-2 py-1.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
          autoFocus
          onKeyDown={(e) => {
            if (e.key === 'Enter' && name.trim()) {
              onSave(name.trim());
            }
            if (e.key === 'Escape') {
              onCancel();
            }
          }}
        />
        <button
          onClick={() => name.trim() && onSave(name.trim())}
          disabled={!name.trim()}
          className="px-3 py-1.5 rounded bg-primary text-primary-foreground text-sm hover:bg-primary/90 disabled:opacity-50"
        >
          Save
        </button>
        <button
          onClick={onCancel}
          className="px-3 py-1.5 rounded bg-secondary text-foreground text-sm hover:bg-secondary/80"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

function LoadDialog({ onClose }: { onClose: () => void }) {
  const { savedScripts, loadScript, deleteScript } = useBrowserStore();

  if (savedScripts.length === 0) {
    return (
      <div className="p-3 border-t border-border">
        <p className="text-sm text-muted-foreground text-center py-4">No saved scripts</p>
        <button
          onClick={onClose}
          className="w-full px-3 py-1.5 rounded bg-secondary text-foreground text-sm hover:bg-secondary/80"
        >
          Close
        </button>
      </div>
    );
  }

  return (
    <div className="border-t border-border">
      <div className="p-3 pb-2">
        <p className="text-xs text-muted-foreground mb-2">Saved scripts:</p>
      </div>
      <div className="max-h-32 overflow-y-auto px-3">
        {savedScripts.map((script) => (
          <div
            key={script.id}
            className="flex items-center gap-2 py-1.5 hover:bg-secondary/50 rounded px-2 -mx-2"
          >
            <button
              onClick={() => {
                loadScript(script);
                onClose();
              }}
              className="flex-1 text-left"
            >
              <span className="text-sm text-foreground">{script.name}</span>
              <span className="text-xs text-muted-foreground ml-2">
                ({script.steps.length} steps)
              </span>
            </button>
            <button
              onClick={() => deleteScript(script.id)}
              className="p-1 rounded hover:bg-secondary text-muted-foreground hover:text-red-400"
            >
              <Trash2 className="h-3 w-3" />
            </button>
          </div>
        ))}
      </div>
      <div className="p-3 pt-2">
        <button
          onClick={onClose}
          className="w-full px-3 py-1.5 rounded bg-secondary text-foreground text-sm hover:bg-secondary/80"
        >
          Close
        </button>
      </div>
    </div>
  );
}

export function ScriptRecorder() {
  const {
    isRecording,
    isBrowserRecording,
    currentScript,
    isPlaying,
    playingStepIndex,
    startRecording,
    stopRecording,
    clearScript,
    saveScript,
    playScript,
    stopPlaying,
    removeStep,
    updateStepDelay,
    savedScripts,
  } = useBrowserStore();

  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [showLoadDialog, setShowLoadDialog] = useState(false);

  const handleSave = async (name: string) => {
    await saveScript(name);
    setShowSaveDialog(false);
  };

  return (
    <div className="bg-card border border-border rounded-lg flex flex-col flex-1 min-h-0">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border">
        <h2 className="text-sm font-medium text-foreground">Script Recorder</h2>
        <div className="flex items-center gap-1">
          {/* Record/Stop */}
          {isRecording ? (
            <button
              onClick={stopRecording}
              className="flex items-center gap-1.5 px-2 py-1 rounded bg-red-500/20 text-red-400 hover:bg-red-500/30 text-xs"
            >
              <Square className="h-3 w-3 fill-current" />
              Stop
            </button>
          ) : (
            <button
              onClick={startRecording}
              disabled={isPlaying}
              className="flex items-center gap-1.5 px-2 py-1 rounded bg-red-500/20 text-red-400 hover:bg-red-500/30 disabled:opacity-50 text-xs"
            >
              <Circle className="h-3 w-3 fill-current" />
              Record
            </button>
          )}

          {/* Play/Stop */}
          {isPlaying ? (
            <button
              onClick={stopPlaying}
              className="flex items-center gap-1.5 px-2 py-1 rounded bg-yellow-500/20 text-yellow-400 hover:bg-yellow-500/30 text-xs"
            >
              <Square className="h-3 w-3 fill-current" />
              Stop
            </button>
          ) : (
            <button
              onClick={playScript}
              disabled={currentScript.length === 0 || isRecording}
              className="flex items-center gap-1.5 px-2 py-1 rounded bg-green-500/20 text-green-400 hover:bg-green-500/30 disabled:opacity-50 text-xs"
            >
              <Play className="h-3 w-3 fill-current" />
              Play
            </button>
          )}
        </div>
      </div>

      {/* Script Steps */}
      <div className="flex-1 h-[300px] max-h-[400px] overflow-y-auto px-3 py-2">
        {currentScript.length === 0 ? (
          <div className="flex items-center justify-center h-20">
            <p className="text-sm text-muted-foreground text-center">
              {isRecording ? (
                <>
                  {isBrowserRecording && (
                    <span className="block text-green-400 mb-1">● Recording from browser</span>
                  )}
                  Interact with the browser or click elements here
                </>
              ) : (
                'No steps recorded'
              )}
            </p>
          </div>
        ) : (
          <div className="space-y-0.5">
            {currentScript.map((step, index) => (
              <StepItem
                key={step.id}
                step={step}
                index={index}
                isPlaying={isPlaying}
                isCurrentStep={isPlaying && playingStepIndex === index}
                onRemove={() => removeStep(step.id)}
                onUpdateDelay={(delay) => updateStepDelay(step.id, delay)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Actions */}
      {!showSaveDialog && !showLoadDialog && currentScript.length > 0 && (
        <div className="flex items-center gap-2 px-3 py-2 border-t border-border">
          <button
            onClick={() => setShowSaveDialog(true)}
            disabled={isRecording || isPlaying}
            className="flex items-center gap-1.5 px-2 py-1 rounded bg-secondary text-foreground hover:bg-secondary/80 disabled:opacity-50 text-xs"
          >
            <Save className="h-3 w-3" />
            Save
          </button>
          <button
            onClick={() => setShowLoadDialog(true)}
            disabled={isRecording || isPlaying}
            className="flex items-center gap-1.5 px-2 py-1 rounded bg-secondary text-foreground hover:bg-secondary/80 disabled:opacity-50 text-xs"
          >
            <FolderOpen className="h-3 w-3" />
            Load
            {savedScripts.length > 0 && (
              <span className="text-muted-foreground">({savedScripts.length})</span>
            )}
          </button>
          <div className="flex-1" />
          <button
            onClick={clearScript}
            disabled={isRecording || isPlaying}
            className="flex items-center gap-1.5 px-2 py-1 rounded text-muted-foreground hover:text-red-400 hover:bg-secondary disabled:opacity-50 text-xs"
          >
            <Trash2 className="h-3 w-3" />
            Clear
          </button>
        </div>
      )}

      {/* Load when no script */}
      {!showSaveDialog && !showLoadDialog && currentScript.length === 0 && savedScripts.length > 0 && !isRecording && (
        <div className="flex items-center gap-2 px-3 py-2 border-t border-border">
          <button
            onClick={() => setShowLoadDialog(true)}
            className="flex items-center gap-1.5 px-2 py-1 rounded bg-secondary text-foreground hover:bg-secondary/80 text-xs"
          >
            <FolderOpen className="h-3 w-3" />
            Load Script
            <span className="text-muted-foreground">({savedScripts.length})</span>
          </button>
        </div>
      )}

      {/* Save Dialog */}
      {showSaveDialog && (
        <SaveDialog onSave={handleSave} onCancel={() => setShowSaveDialog(false)} />
      )}

      {/* Load Dialog */}
      {showLoadDialog && <LoadDialog onClose={() => setShowLoadDialog(false)} />}
    </div>
  );
}
